/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/hold-points/QaHoldPointSignoff.tsx
 * FILE_ID: JOBHUB-QA-HOLD-SIGNOFF-001
 * PURPOSE: Run-row sign-off strip for a check marked Needs sign-off:
 *          Request sign-off, then Sign off or Reject, then the signature.
 * WHY: Audit item 2 (change #1077). Hold parks a check; sign-off keeps it in
 *      Run and the ITP. Phone Run renders the same row, so this is one control.
 */
import { lazy, Suspense } from 'react'
import { Loader2, ShieldCheck } from 'lucide-react'
import type { QaApi, QaHoldPoint, QaItemInstance } from '../../../qa/qaApi'
import type { QaItemSignoff } from '../../../qa/qaHoldPoints'
import { useQaHoldPointSignoff } from './useQaHoldPointSignoff'

const QaItpSignaturePad = lazy(() => import('../itp-export/QaItpSignaturePad'))

const BUTTON = 'rounded-md border border-border px-2 py-1 text-[11px] disabled:opacity-50'
const INPUT = 'min-h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs'

function shortDate(at?: string | null): string {
  if (!at) return ''
  const date = new Date(at)
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

function SignoffSummary({ signoff }: { signoff: QaItemSignoff }) {
  const { state, holdPoint, signature } = signoff
  if (state === 'approved') {
    const who = [signature?.signer_name || holdPoint?.approved_by_name, signature?.signer_role].filter(Boolean).join(', ')
    const when = shortDate(signature?.signed_at || holdPoint?.approved_at)
    return (
      <span className="flex flex-wrap items-center gap-2 text-status-good">
        <span>{`Signed off${who ? ` by ${who}` : ''}${when ? ` · ${when}` : ''}`}</span>
        {signature?.signature_url ? (
          <img
            src={signature.signature_url}
            alt={`Signature of ${signature.signer_name}`}
            className="h-8 w-auto rounded border border-border bg-white"
          />
        ) : null}
      </span>
    )
  }
  if (state === 'requested') {
    const by = holdPoint?.requested_by_name
    return <span className="text-status-warn">{`Awaiting sign-off${by ? ` · requested by ${by}` : ''}`}</span>
  }
  if (state === 'rejected') {
    const by = holdPoint?.rejected_by_name
    const why = holdPoint?.decision_comment
    return <span className="text-status-bad">{`Sign-off rejected${by ? ` by ${by}` : ''}${why ? `: ${why}` : ''}`}</span>
  }
  return <span className="text-muted-foreground">Needs sign-off</span>
}

export function QaHoldPointSignoff({
  item,
  areaId,
  jobId,
  holdPoints,
  canSign,
  api,
  onMutated,
  onWriteError,
}: {
  item: QaItemInstance
  areaId: string
  jobId: number
  holdPoints: readonly QaHoldPoint[]
  canSign: boolean
  api: Pick<QaApi, 'requestHoldPoint' | 'approveHoldPoint' | 'rejectHoldPoint' | 'uploadFile'>
  onMutated: () => void
  onWriteError?: (message: string) => void
}) {
  const flow = useQaHoldPointSignoff({
    item, areaId, jobId, holdPoints, canSign, api, onMutated, onWriteError,
  })
  if (flow.signoff.state === 'none') return null

  return (
    <div
      data-testid={`qa-signoff-${item.id}`}
      data-signoff-state={flow.signoff.state}
      className="mt-1.5 space-y-1.5 rounded-md border border-border px-2 py-1.5 text-[11px]"
    >
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <SignoffSummary signoff={flow.signoff} />
        {flow.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden /> : null}
        {flow.canRequest ? (
          <button type="button" disabled={flow.busy} onClick={flow.request} className={BUTTON}>
            {flow.signoff.state === 'rejected' ? 'Request sign-off again' : 'Request sign-off'}
          </button>
        ) : null}
        {flow.canDecide ? (
          <>
            <button type="button" disabled={flow.busy} onClick={flow.startSign} className={`${BUTTON} text-primary`}>
              Sign off
            </button>
            <button type="button" disabled={flow.busy} onClick={flow.startReject} className={BUTTON}>
              Reject
            </button>
          </>
        ) : null}
      </div>

      {flow.mode === 'sign' || flow.mode === 'pad' ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            value={flow.signerName}
            onChange={(event) => flow.setSignerName(event.target.value)}
            placeholder="Your name"
            aria-label={`Signer name for ${item.title}`}
            className={INPUT}
          />
          <input
            value={flow.signerRole}
            onChange={(event) => flow.setSignerRole(event.target.value)}
            placeholder="Role"
            aria-label={`Signer role for ${item.title}`}
            className={INPUT}
          />
          <button
            type="button"
            disabled={flow.busy || !flow.signerName.trim()}
            onClick={flow.openPad}
            className="rounded-md bg-primary px-2 py-1 text-[11px] text-primary-foreground disabled:opacity-50"
          >
            Sign
          </button>
          <button type="button" disabled={flow.busy} onClick={flow.cancel} className={BUTTON}>Cancel</button>
        </div>
      ) : null}

      {flow.mode === 'reject' ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            value={flow.comment}
            onChange={(event) => flow.setComment(event.target.value)}
            placeholder="Why is it rejected?"
            aria-label={`Rejection reason for ${item.title}`}
            className={INPUT}
          />
          <button
            type="button"
            disabled={flow.busy || !flow.comment.trim()}
            onClick={flow.reject}
            className={`${BUTTON} text-destructive`}
          >
            Reject
          </button>
          <button type="button" disabled={flow.busy} onClick={flow.cancel} className={BUTTON}>Cancel</button>
        </div>
      ) : null}

      {flow.error ? <p role="alert" className="text-destructive">{flow.error}</p> : null}

      {flow.mode === 'pad' ? (
        <Suspense fallback={null}>
          <QaItpSignaturePad onCancel={flow.closePad} onSaved={flow.onPadSaved} />
        </Suspense>
      ) : null}
    </div>
  )
}
